import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSEO from '../../../hooks/useSEO';
import AnswerCapsule from '../../../components/AnswerCapsule';
import { blogAuthor } from '../../../utils/blogJsonLd';
import '../hubs/VehicleHub.css';
import '../TPermitHub.css';
import { STATES } from './stateData';

const BASE_URL = 'https://www.buyselltaxi.com';
const HUB_URL = `${BASE_URL}/t-permit`;

// Same contract as the hub: visible in the hero and emitted as schema dates.
// A state may override either in stateData.js when its copy is revised.
const PUBLISHED = '2026-09-07';
const UPDATED = '2026-09-07';
const fmtDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

// Spoke pages under the /t-permit hub, one per state where BST actually holds
// stock. Permit rules are administered per state, so this is the level at which
// the reader's question ("can I run this car on my route?") is answerable.
//
// Schema mirrors the hub deliberately: BreadcrumbList + Article + ItemList, and
// NO FAQPage — Google retired FAQ rich results for all sites on 2026-05-07, so
// new FAQ markup earns nothing. No second AutoDealer or LocalBusiness either;
// public/index.html already ships the sitewide entity, and duplicating it is the
// defect that had to be removed from the city pages.
const TPermitState = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace('/t-permit/by-state/', '').replace(/\/$/, '');
  const state = STATES[slug];

  if (!state) return <NotAState />;
  return <StateView state={state} />;
};

const NotAState = () => {
  useSEO({ title: 'T-Permit by State', noindex: true, follow: true });
  return (
    <div className="hub-page tpermit-hub">
      <div className="tpermit-body">
        <p>That state guide does not exist. <Link to="/t-permit">Back to the T-permit guide</Link>.</p>
      </div>
    </div>
  );
};

const StateView = ({ state }) => {
  const pageUrl = `${BASE_URL}/t-permit/by-state/${state.slug}`;
  const answer = `A T-permit in ${state.name} is a state commercial transport permit that lets a vehicle carry passengers for hire, run on a ${state.plate}. It is issued by the state transport authority, and it stops at the state border — crossing it regularly needs an All India Tourist Permit as well.`;

  const updated = state.updatedAt || UPDATED;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'T-Permit Guide', item: HUB_URL },
        { '@type': 'ListItem', position: 3, name: `${state.name} T-Permit Rules`, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `T-Permit Rules in ${state.name}`,
      description: state.description,
      about: { '@type': 'Thing', name: `Commercial transport permit (${state.name}, India)` },
      mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
      url: pageUrl,
      inLanguage: 'en-IN',
      isPartOf: { '@type': 'WebPage', '@id': HUB_URL },
      datePublished: state.publishedAt || PUBLISHED,
      dateModified: updated,
      author: blogAuthor({}),
      publisher: {
        '@type': 'Organization',
        name: 'Buy Sell Taxi',
        url: BASE_URL,
        logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `${state.name} permit guides`,
      url: pageUrl,
      numberOfItems: state.guides.length,
      itemListElement: state.guides.map(([s, t], i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${BASE_URL}/blog/${s}`,
        name: t,
      })),
    },
  ];

  useSEO({
    // useSEO appends ' | Buy Sell Taxi' (16 chars); titles here are <= 44.
    title: state.title,
    description: state.description,
    keywords: state.keywords,
    url: pageUrl,
    image: `${BASE_URL}/og/landing/t-permit/by-state/${state.slug}.png`,
    type: 'article',
    jsonLd,
  });

  const [cityPath, cityLabel] = state.cityLink;

  return (
    <div className="hub-page tpermit-hub">

      <section className="hub-hero">
        <div className="hub-hero-inner">
          <nav className="hub-breadcrumb">
            <Link to="/">Home</Link> <span>/</span>
            <Link to="/t-permit">T-Permit Guide</Link> <span>/</span>
            <span>{state.name}</span>
          </nav>
          <h1>T-Permit Rules in {state.name}</h1>
          <p className="tpermit-standfirst">{state.standfirst}</p>
          <p className="tpermit-byline">
            By the Buy Sell Taxi team · Updated <time dateTime={updated}>{fmtDate(updated)}</time>
          </p>
        </div>
      </section>

      <div className="tpermit-body">

        <AnswerCapsule question={`What is a T-permit in ${state.name}?`} answer={answer} />

        <section className="hub-section">
          {state.intro.map((p, i) => <p key={i}>{p}</p>)}
        </section>

        <section className="hub-section">
          <h2>{state.name} at a glance</h2>
          <div className="tpermit-facts">
            <table>
              <tbody>
                {state.facts.map(([k, v]) => (
                  <tr key={k}><th scope="row">{k}</th><td>{v}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* The commercial bridge sits ABOVE the guide library, matching the hub.
            A permit searcher is the closest thing to qualified demand this site
            gets, and this block is the only thing on the page that converts a
            regulatory question into an enquiry. Do not move it below. */}
        <section className="hub-section tpermit-commercial">
          <h2>Vehicles that already hold a permit</h2>
          <p>
            Every listing on Buy Sell Taxi is a commercial vehicle, so the permit question is
            answered before you start. Browse {' '}
            <Link to={cityPath}>{cityLabel}</Link>, or see{' '}
            <Link to="/vehicles">all used taxis and commercial vehicles</Link>.
          </p>
          <p>
            Selling one instead? <Link to="/sell-taxi">List it free</Link> — permit status is
            part of what buyers here are shopping for.
          </p>
        </section>

        <section className="hub-section">
          <h2>Guides for {state.name} operators</h2>
          <p className="hub-section-blurb">
            These answer the questions {state.name} buyers ask most: {state.faqIntent.join('; ')}.
          </p>
          <ul className="tpermit-list">
            {state.guides.map(([s, t]) => (
              <li key={s}><Link to={`/blog/${s}`}>{t}</Link></li>
            ))}
          </ul>
        </section>

        <section className="hub-section">
          <h2>Other states</h2>
          <ul className="tpermit-list">
            {Object.values(STATES)
              .filter((s) => s.slug !== state.slug)
              .map((s) => (
                <li key={s.slug}>
                  <Link to={`/t-permit/by-state/${s.slug}`}>T-permit rules in {s.name}</Link>
                </li>
              ))}
            <li><Link to="/t-permit">The complete T-permit guide</Link></li>
          </ul>
        </section>

      </div>
    </div>
  );
};

export default TPermitState;
